import { DEFAULT_VIEW, fitRect, imageRect, type ViewState } from './view-transform.js';

export class CanvasRenderer {
  private readonly context: CanvasRenderingContext2D;
  private dpr = 1;

  public constructor(private readonly canvas: HTMLCanvasElement) {
    const context = canvas.getContext('2d');
    if (context === null) {
      throw new Error('Canvas 2D context unavailable');
    }
    this.context = context;
  }

  public resize(width: number, height: number, dpr: number): boolean {
    this.dpr = dpr;
    const targetWidth = Math.max(1, Math.round(width * dpr));
    const targetHeight = Math.max(1, Math.round(height * dpr));
    if (this.canvas.width === targetWidth && this.canvas.height === targetHeight) {
      return false;
    }
    this.canvas.width = targetWidth;
    this.canvas.height = targetHeight;
    return true;
  }

  public viewportSize(): { width: number; height: number } {
    return { width: this.canvas.width / this.dpr, height: this.canvas.height / this.dpr };
  }

  public draw(layers: (ImageBitmap | null)[], view: ViewState = DEFAULT_VIEW): void {
    const { width, height } = this.canvas;
    this.context.setTransform(1, 0, 0, 1, 0, 0);
    this.context.clearRect(0, 0, width, height);
    const visible = layers.filter((layer): layer is ImageBitmap => layer !== null);
    const reference = visible[0];
    if (reference === undefined) {
      return;
    }
    const viewport = this.viewportSize();
    const fit = fitRect(viewport, reference);
    const rect = imageRect(view, fit, viewport);
    const scale = rect.width / reference.width;
    this.context.imageSmoothingEnabled = scale < 1;
    this.context.imageSmoothingQuality = 'high';
    this.context.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    for (const layer of visible) {
      this.context.drawImage(layer, rect.x, rect.y, rect.width, rect.height);
    }
  }
}
